/**
 * Intranet Osasco Plaza — src/modules/seg.jsx
 * Telas do módulo Segurança Patrimonial (ativos, vistorias, grupos, tipos).
 * Extraído de app.jsx — ver DOC/ADR/0011.
 */

import { Blocked, Icon, calcProxima, fmtDate, hasPerm, segDueStatus } from './shared.jsx';
import { baixarQRPng, imprimirQR, qrGenerateSVG } from './qr.js';
const { useState, useEffect, useRef } = React;

/* Mesmas opções que o worker aceita (ver AVISOS_DIAS em seg-worker.js) —
   com quantos dias de antecedência o status vira 'warn' quando a Data de
   Validade está preenchida. */
const AVISOS_DIAS_OPCOES = [15, 30, 35, 60];

/* ── QR Code do Ativo ──
   O conteúdo do QR é a própria URL do app com ?ativo=<codigo> — deep-link
   que o App() (app.jsx) captura no boot e, assim que a pessoa estiver
   logada, leva direto para "Registrar Vistoria" com este ativo já
   selecionado (ver DOC/ADR/0012). Diferente do VicStaffO, aqui não existe
   rota pública nem chave de acesso compartilhada: quem escaneia usa o
   login normal da Intranet — a mesma autorização de sempre. */
function SegQRModal({ ativo, onClose }) {
  const url = `${window.location.origin}/?ativo=${encodeURIComponent(ativo.codigo)}`;
  const svg = qrGenerateSVG(url, { size: 220, margin: 3, level: 'M' });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  const baixar = async () => {
    setBusy(true); setErr('');
    try { await baixarQRPng(svg, `qr-${ativo.codigo}.png`, 512); }
    catch (e) { setErr(e.message || 'Não foi possível gerar o PNG.'); }
    setBusy(false);
  };
  const imprimir = () => imprimirQR(svg, `${ativo.codigo} — ${ativo.nome}`);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:340,textAlign:'center'}}>
        <button className="close-x" onClick={onClose}><Icon name="x" size={16}/></button>
        <h3>QR Code do Ativo</h3>
        <p style={{fontSize:13,color:'var(--ink-3)',marginBottom:16}}>#{ativo.codigo} — {ativo.nome}</p>
        <div style={{display:'flex',justifyContent:'center',marginBottom:16}} dangerouslySetInnerHTML={{__html: svg}}/>
        <p style={{fontSize:11,color:'var(--ink-4)',marginBottom:16}}>
          Aponte a câmera do celular para abrir o registro de vistoria deste ativo (é preciso estar logado na Intranet).
        </p>
        {err && <p style={{color:'var(--err)',fontSize:12,marginBottom:12}}>{err}</p>}
        <div style={{display:'flex',gap:10,justifyContent:'center'}}>
          <button className="btn btn-ghost" onClick={imprimir}><Icon name="printer" size={14}/> Imprimir</button>
          <button className="btn btn-primary" onClick={baixar} disabled={busy}><Icon name="download" size={14}/> {busy?'Gerando...':'Baixar PNG'}</button>
        </div>
      </div>
    </div>
  );
}


/* Busca a foto da vistoria como blob autenticado e mostra como <img>.
   `URL.createObjectURL` fica preso em memória até `revokeObjectURL` — o
   cleanup do efeito cuida disso ao trocar de vistoria ou sair da tela. Sem
   isso, uma sessão longa abrindo muitas fotos vazaria memória aos poucos. */
export function SegFoto({ vistoriaId, className, onClick, alt }) {
  const [url, setUrl] = useState(null);
  const [erro, setErro] = useState(false);
  useEffect(() => {
    let vivo = true, criado = null;
    API.seg.fotoUrl(vistoriaId)
      .then(u => { if (vivo) { criado = u; setUrl(u); } else URL.revokeObjectURL(u); })
      .catch(() => { if (vivo) setErro(true); });
    return () => { vivo = false; if (criado) URL.revokeObjectURL(criado); };
  }, [vistoriaId]);
  if (erro) return null;
  if (!url) return <div className={className} style={{ background:'var(--surface-2)', display:'flex', alignItems:'center', justifyContent:'center' }}><Icon name="camera" size={16} style={{ color:'var(--ink-4)' }}/></div>;
  return <img src={url} alt={alt||'Foto da vistoria'} className={className} onClick={onClick}/>;
}


/* ── Ativos Screen ── */
export function SegAtivosScreen({ user }) {
  const [ativos, setAtivos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [tipos, setTipos]   = useState([]);
  const [lojas, setLojas]   = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga]   = useState('');
  const [modal, setModal]   = useState(null); // null | 'new' | ativo
  const [sel, setSel]       = useState(null); // ativo selecionado para ver vistorias
  const [qrAtivo, setQrAtivo] = useState(null); // ativo com o modal de QR aberto
  const [form, setForm]     = useState({ codigo:'', nome:'', localizacao:'', tipoId:'', grupoId:'', lojaId:'', periodicidade:12, periodicidadeDias:null, dataValidade:'', avisoDias:30 });
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [filtroLoja, setFiltroLoja] = useState('todos'); // 'todos' | 'comum' | <lojaId>

  const canEdit = hasPerm(user,'seguranca','edit');

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try {
      const [a, g, t, l] = await Promise.all([API.seg.ativos(), API.seg.grupos(), API.seg.tipos(), API.listarLojas()]);
      setAtivos(a.ativos||[]); setGrupos(g.grupos||[]); setTipos(t.tipos||[]);
      setLojas((l.lojas||[]).filter(x=>x.ativo!==0));
    } catch (e) {
      setErroCarga(e.message || 'Não foi possível carregar os ativos.');
    }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => {
    setForm({ codigo:'', nome:'', localizacao:'', tipoId: tipos[0]?.id||'', grupoId: grupos[0]?.id||'', lojaId:'', periodicidade:12, periodicidadeDias:null, dataValidade:'', avisoDias:30 });
    setErr(''); setModal('new');
  };

  const openEdit = (a) => {
    setForm({ codigo:a.codigo, nome:a.nome, localizacao:a.localizacao, tipoId:a.tipoId||'', grupoId:a.grupoId, lojaId:a.lojaId||'', periodicidade:a.periodicidade||12, periodicidadeDias:a.periodicidadeDias||null, dataValidade:a.dataValidade||'', avisoDias:a.avisoDias||30 });
    setErr(''); setModal(a);
  };

  const save = async () => {
    if (!form.nome.trim() || !form.codigo.trim()) { setErr('Preencha código e nome.'); return; }
    if (!form.grupoId) { setErr('Selecione o grupo de controle.'); return; }
    setBusy(true); setErr('');
    try {
      const payload = { ...form, tipoId: form.tipoId || null, lojaId: form.lojaId || null };
      if (!payload.periodicidadeDias) delete payload.periodicidadeDias; // mutuamente exclusiva com periodicidade (meses) — ver seg-worker.js
      if (modal === 'new') await API.seg.criarAtivo(payload);
      else                 await API.seg.editarAtivo(modal.id, payload);
      await refresh(); setModal(null);
    } catch (e) {
      setErr(e.message || 'Não foi possível salvar o ativo.');
    }
    setBusy(false);
  };

  const del = async (id) => {
    if (!confirm('Remover este ativo? O histórico de vistorias dele some junto.')) return;
    try { await API.seg.removerAtivo(id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível remover o ativo.'); }
  };

  const filtered = ativos.filter(a => {
    if (filtro === 'vencidos' && segDueStatus(a).cls !== 'err') return false;
    if (filtro === 'proximos' && segDueStatus(a).cls !== 'warn') return false;
    if (filtroLoja === 'comum' && a.lojaId) return false;
    if (filtroLoja !== 'todos' && filtroLoja !== 'comum' && a.lojaId !== filtroLoja) return false;
    return true;
  });

  if (!hasPerm(user,'seguranca','view')) return <Blocked/>;
  if (sel) return <SegAtivoDetalhe ativo={sel} user={user} onBack={() => { setSel(null); refresh(); }}/>;

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title">
          <h2>🔴 Ativos de Segurança</h2>
          <p>Gestão de extintores, sensores, saídas de emergência e demais ativos</p>
        </div>
        {canEdit && <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/> Novo Ativo</button>}
      </div>

      {carregando && <div className="card" style={{ padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16 }}>Carregando ativos…</div>}
      {erroCarga && (
        <div className="card" style={{ padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2' }}>
          <div style={{ color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6 }}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && <>
      {/* Estatísticas rápidas */}
      <div className="seg-stat-row">
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--ink)'}}>{ativos.length}</div><div className="sl">Total de ativos</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--ok)'}}>{ativos.filter(a=>segDueStatus(a).cls==='ok').length}</div><div className="sl">Em dia</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--warn)'}}>{ativos.filter(a=>segDueStatus(a).cls==='warn').length}</div><div className="sl">Próximos do vencimento</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--err)'}}>{ativos.filter(a=>segDueStatus(a).cls==='err').length}</div><div className="sl">Vencidos</div></div>
      </div>

      {/* Filtros */}
      <div className="seg-tabs" style={{display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}}>
        {[['todos','Todos'],['proximos','⚠ Próximos'],['vencidos','🔴 Vencidos']].map(([k,l]) => (
          <button key={k} className={`seg-tab ${filtro===k?'active':''}`} onClick={()=>setFiltro(k)}>{l}</button>
        ))}
        <select style={{height:32,fontSize:12,borderRadius:6,border:'1px solid var(--line)',padding:'0 8px',background:'var(--surface-2)',marginLeft:'auto'}} value={filtroLoja} onChange={e=>setFiltroLoja(e.target.value)}>
          <option value="todos">Todas as lojas</option>
          <option value="comum">🏢 Comum — Área do Shopping</option>
          {lojas.map(l=><option key={l.id} value={l.id}>{l.nome}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{textAlign:'center',padding:40,color:'var(--ink-3)'}}>
          <Icon name="shield" size={32} style={{margin:'0 auto 12px',display:'block'}}/>
          <p>Nenhum ativo encontrado.</p>
          {canEdit && <button className="btn btn-primary" style={{marginTop:16}} onClick={openNew}><Icon name="plus" size={14}/> Cadastrar primeiro ativo</button>}
        </div>
      ) : (
        <div className="seg-grid">
          {filtered.map(a => {
            const due = segDueStatus(a);
            return (
              <div key={a.id} className="seg-asset-card" onClick={() => setSel(a)}>
                <div className="seg-asset-header">
                  <div>
                    <div className="seg-asset-id">#{a.codigo}</div>
                    <div className="seg-asset-name">{a.nome}</div>
                    <div className="seg-asset-loc"><Icon name="map-pin" size={11}/>{a.localizacao}</div>
                  </div>
                  <span className={`seg-due-pill ${due.cls}`}>{due.label}</span>
                </div>
                <div className="seg-asset-meta">
                  <span className="seg-meta-pill">{a.lojaNome ? <>🏬 {a.lojaNome}</> : <><Icon name="building" size={11}/>Comum</>}</span>
                  {a.tipoNome && <span className="seg-meta-pill"><Icon name="fire" size={11}/>{a.tipoNome}</span>}
                  {a.grupoNome && <span className="seg-meta-pill group" style={{background:(a.grupoCor||'#2563eb')+'22',color:a.grupoCor||'#2563eb'}}>{a.grupoNome}</span>}
                  <span className="seg-meta-pill"><Icon name="file-text" size={11}/>{a.totalVistorias||0} vistoria(s)</span>
                  {(a.periodicidadeDias || a.periodicidade) && <span className="seg-meta-pill"><Icon name="clock" size={11}/>{
                    a.periodicidadeDias ? (a.periodicidadeDias===1?'Diária':a.periodicidadeDias===7?'Semanal':a.periodicidadeDias===15?'Quinzenal':'A cada '+a.periodicidadeDias+'d')
                    : (a.periodicidade===1?'Mensal':a.periodicidade===2?'Bimestral':a.periodicidade===3?'Trimestral':a.periodicidade===6?'Semestral':a.periodicidade===12?'Anual':a.periodicidade===24?'Bianual':'A cada '+a.periodicidade+'m')
                  }</span>}
                  {a.proximaVistoria && <span className="seg-meta-pill"><Icon name="calendar" size={11}/>Próx: {fmtDate(a.proximaVistoria)}</span>}
                </div>
                <div style={{display:'flex',gap:8,marginTop:12}} onClick={e=>e.stopPropagation()}>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setQrAtivo(a)} title="QR Code"><Icon name="qr-code" size={13}/></button>
                  {canEdit && <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(a)}><Icon name="edit" size={13}/></button>}
                  {canEdit && <button className="btn btn-danger btn-sm" onClick={()=>del(a.id)}><Icon name="trash" size={13}/></button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>}

      {/* Modal novo/editar ativo */}
      {modal && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:520}}>
            <button className="close-x" onClick={()=>setModal(null)}><Icon name="x" size={16}/></button>
            <h3>{modal==='new'?'Novo Ativo':'Editar Ativo'}</h3>
            <p style={{fontSize:13,color:'var(--ink-3)',marginBottom:20}}>Preencha os dados do ativo de segurança</p>
            <div className="seg-field-row">
              <div className="field"><label>Código / ID <span className="req">*</span></label><input value={form.codigo} onChange={e=>setForm(f=>({...f,codigo:e.target.value}))} placeholder="EXT-001"/></div>
              <div className="field"><label>Tipo de Ativo</label>
                <select value={form.tipoId} onChange={e=>setForm(f=>({...f,tipoId:e.target.value}))}>
                  <option value="">Selecione...</option>
                  {tipos.map(t=><option key={t.id} value={t.id}>{t.icone} {t.nome}</option>)}
                </select>
              </div>
            </div>
            <div className="field"><label>Nome / Descrição <span className="req">*</span></label><input value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} placeholder="Ex: Extintor CO₂ 6kg"/></div>
            <div className="field"><label>Localização</label><input value={form.localizacao} onChange={e=>setForm(f=>({...f,localizacao:e.target.value}))} placeholder="Ex: Piso 2 — próx. escada B"/></div>
            <div className="field"><label>Loja</label>
              <select value={form.lojaId} onChange={e=>setForm(f=>({...f,lojaId:e.target.value}))}>
                <option value="">🏢 Comum — Área do Shopping</option>
                {lojas.map(l=><option key={l.id} value={l.id}>{l.nome}</option>)}
              </select>
              <div className="hint">Deixe em "Comum" para ativos do shopping (câmeras, sprinklers, hidrantes). Escolha a loja para ativos que pertencem só a ela (extintor, laudo de exaustão, alvará) — o nome dela não precisa entrar no campo Nome.</div>
            </div>
            <div className="seg-field-row">
              <div className="field"><label>Grupo de Controle <span className="req">*</span></label>
                <select value={form.grupoId} onChange={e=>setForm(f=>({...f,grupoId:e.target.value}))}>
                  <option value="">Selecione...</option>
                  {grupos.map(g=><option key={g.id} value={g.id}>{g.nome}</option>)}
                </select>
              </div>
              <div className="field"><label>Periodicidade da Vistoria</label>
                <select
                  value={form.periodicidadeDias ? `dias:${form.periodicidadeDias}` : `meses:${form.periodicidade}`}
                  onChange={e=>{
                    const [unidade, valorStr] = e.target.value.split(':');
                    const valor = parseInt(valorStr, 10);
                    if (unidade === 'dias') setForm(f=>({...f, periodicidadeDias:valor, periodicidade:12}));
                    else setForm(f=>({...f, periodicidade:valor, periodicidadeDias:null}));
                  }}
                >
                  <optgroup label="Prazo curto">
                    <option value="dias:1">Diária (1 dia)</option>
                    <option value="dias:7">Semanal (7 dias)</option>
                    <option value="dias:15">Quinzenal (15 dias)</option>
                  </optgroup>
                  <optgroup label="Prazo padrão">
                    <option value="meses:1">Mensal (1 mês)</option>
                    <option value="meses:2">Bimestral (2 meses)</option>
                    <option value="meses:3">Trimestral (3 meses)</option>
                    <option value="meses:6">Semestral (6 meses)</option>
                    <option value="meses:12">Anual (12 meses)</option>
                    <option value="meses:24">Bianual (24 meses)</option>
                  </optgroup>
                </select>
              </div>
            </div>
            <div className="seg-field-row">
              <div className="field">
                <label>Data de Validade</label>
                <input type="date" value={form.dataValidade} onChange={e=>setForm(f=>({...f,dataValidade:e.target.value}))}/>
                <div className="hint">Opcional — ex: vencimento da recarga do extintor. Se preenchida, manda no status do ativo, independente de vistoria registrada.</div>
              </div>
              <div className="field">
                <label>Aviso de Monitoramento</label>
                <select value={form.avisoDias} onChange={e=>setForm(f=>({...f,avisoDias:parseInt(e.target.value)}))}>
                  {AVISOS_DIAS_OPCOES.map(d=><option key={d} value={d}>{d} dias antes</option>)}
                </select>
                <div className="hint">Com quantos dias de antecedência o painel avisa que a validade está vencendo.</div>
              </div>
            </div>
            {err && <p style={{color:'var(--err)',fontSize:12,marginBottom:12}}>{err}</p>}
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Salvando...':'Salvar Ativo'}</button>
            </div>
          </div>
        </div>
      )}

      {qrAtivo && <SegQRModal ativo={qrAtivo} onClose={()=>setQrAtivo(null)}/>}
    </div>
  );
}


/* ── Detalhe do Ativo + Vistorias ── */
export function SegAtivoDetalhe({ ativo, user, onBack }) {
  const [vistorias, setVistorias] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [tab, setTab]             = useState('vistorias');
  const [lightbox, setLightbox]   = useState(null);
  const [qrAberto, setQrAberto]   = useState(false);

  const due = segDueStatus(ativo);

  const refresh = async () => {
    setCarregando(true);
    try {
      const d = await API.seg.vistoriasDoAtivo(ativo.id);
      setVistorias(d.vistorias||[]);
    } catch { /* tela de detalhe não precisa travar por isso — a lista fica vazia */ }
    setCarregando(false);
  };
  useEffect(()=>{ refresh(); },[]);

  return (
    <div className="main-pad">
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}><Icon name="arrow-left" size={14}/> Voltar</button>
        <div>
          <div style={{fontSize:11,color:'var(--ink-4)',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em'}}>#{ativo.codigo}</div>
          <h2 style={{fontSize:20,fontWeight:600,margin:0}}>{ativo.nome}</h2>
          <div style={{fontSize:12,color:'var(--ink-3)',marginTop:2,display:'flex',gap:12,flexWrap:'wrap'}}>
            <span><Icon name="map-pin" size={11}/> {ativo.localizacao}</span>
            {ativo.tipoNome && <span><Icon name="fire" size={11}/> {ativo.tipoNome}</span>}
            {ativo.grupoNome && <span style={{color:ativo.grupoCor||'#2563eb',fontWeight:600}}>{ativo.grupoNome}</span>}
            <span>{ativo.lojaNome ? <>🏬 {ativo.lojaNome}</> : <><Icon name="building" size={11}/> Comum — Área do Shopping</>}</span>
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" style={{marginLeft:'auto'}} onClick={()=>setQrAberto(true)}><Icon name="qr-code" size={13}/> QR Code</button>
        <span className={`seg-due-pill ${due.cls}`}>{due.label}</span>
      </div>

      <div className="seg-tabs">
        {[['vistorias','📋 Histórico de Vistorias'],['info','ℹ️ Informações']].map(([k,l])=>(
          <button key={k} className={`seg-tab ${tab===k?'active':''}`} onClick={()=>setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'vistorias' && (
        <div className="card">
          <div className="card-head"><h3>Vistorias Registradas</h3><span style={{fontSize:12,color:'var(--ink-3)'}}>{vistorias.length} registro(s)</span></div>
          {carregando ? (
            <div style={{textAlign:'center',padding:32,color:'var(--ink-3)'}}>Carregando…</div>
          ) : vistorias.length === 0 ? (
            <div style={{textAlign:'center',padding:32,color:'var(--ink-3)'}}>
              <Icon name="camera" size={28} style={{margin:'0 auto 8px',display:'block'}}/>
              <p>Nenhuma vistoria registrada ainda.</p>
            </div>
          ) : vistorias.map(v => (
            <div key={v.id} className="seg-insp-row">
              <div className="seg-insp-header">
                <div>
                  <div className="seg-insp-who">{v.vistoriador}</div>
                  <div className="seg-insp-when">{new Date(v.criadoEm).toLocaleString('pt-BR')} — {v.grupoNome}</div>
                </div>
                <span className={`status-pill ${v.status==='Aprovado'?'ok':v.status==='Reprovado'?'err':'warn'}`}>
                  <span className="ind"/>{v.status}
                </span>
              </div>
              {v.obs && <div className="seg-insp-obs">💬 {v.obs}</div>}
              {v.temFoto && (
                <SegFoto vistoriaId={v.id} className="seg-insp-thumb" onClick={()=>setLightbox(v.id)}/>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'info' && (
        <div className="card">
          <div className="seg-cols2">
            {[['Código',ativo.codigo],['Tipo',ativo.tipoNome||'—'],['Localização',ativo.localizacao],
              ['Grupo',ativo.grupoNome||'—'],
              ['Loja', ativo.lojaNome || 'Comum — Área do Shopping'],
              ['Data de Validade', ativo.dataValidade ? fmtDate(ativo.dataValidade) : '—'],
              ['Próxima Vistoria',fmtDate(ativo.proximaVistoria)],['Cadastrado em',fmtDate(ativo.criadoEm)]
            ].map(([k,v])=>(
              <div key={k}><div style={{fontSize:11,color:'var(--ink-3)',marginBottom:4,fontWeight:600}}>{k}</div><div style={{fontSize:14,color:'var(--ink)'}}>{v||'—'}</div></div>
            ))}
          </div>
        </div>
      )}

      {lightbox && (
        <div className="modal-overlay" onClick={()=>setLightbox(null)}>
          <SegFoto vistoriaId={lightbox} alt="Foto" style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:12,boxShadow:'0 24px 48px rgba(0,0,0,0.4)'}}/>
        </div>
      )}

      {qrAberto && <SegQRModal ativo={ativo} onClose={()=>setQrAberto(false)}/>}
    </div>
  );
}


/* ── Grupos Screen ── */
export function SegGruposScreen({ user }) {
  const [grupos, setGrupos] = useState([]);
  const [ativos, setAtivos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga]   = useState('');
  const [modal, setModal]   = useState(null);
  const [form, setForm]     = useState({ nome:'', responsavel:'', email:'', cor:'#2563eb', prazoMeses:12 });
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try {
      const [g, a] = await Promise.all([API.seg.grupos(), API.seg.ativos()]);
      setGrupos(g.grupos||[]); setAtivos(a.ativos||[]);
    } catch (e) {
      setErroCarga(e.message || 'Não foi possível carregar os grupos.');
    }
    setCarregando(false);
  };
  useEffect(()=>{ refresh(); },[]);

  if (!hasPerm(user,'seguranca','edit')) return <Blocked/>;

  const openNew = () => { setForm({ nome:'', responsavel:'', email:'', cor:'#2563eb', prazoMeses:12 }); setErr(''); setModal('new'); };
  const openEdit = (g) => { setForm({nome:g.nome,responsavel:g.responsavel,email:g.email,cor:g.cor,prazoMeses:g.prazoMeses}); setErr(''); setModal(g); };

  const save = async () => {
    if (!form.nome.trim())  { setErr('Informe o nome do grupo.'); return; }
    if (!form.email.trim()) { setErr('Informe o email do responsável.'); return; }
    setBusy(true); setErr('');
    try {
      if (modal === 'new') await API.seg.criarGrupo(form);
      else                 await API.seg.editarGrupo(modal.id, form);
      await refresh(); setModal(null);
    } catch (e) {
      setErr(e.message || 'Não foi possível salvar o grupo.');
    }
    setBusy(false);
  };

  const del = async (id) => {
    if (!confirm('Remover grupo?')) return;
    try { await API.seg.removerGrupo(id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível remover o grupo.'); }
  };

  const prazoOpts = [1,2,3,6,12,24];

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>👥 Grupos de Controle</h2><p>Equipes responsáveis pela vistoria de ativos</p></div>
        <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/> Novo Grupo</button>
      </div>

      {carregando && <div className="card" style={{ padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16 }}>Carregando grupos…</div>}
      {erroCarga && (
        <div className="card" style={{ padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2' }}>
          <div style={{ color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6 }}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && (
      <div style={{display:'flex',flexDirection:'column',gap:12}}>
        {grupos.map(g=>{
          const ga   = ativos.filter(a=>a.grupoId===g.id);
          const venc = ga.filter(a=>segDueStatus(a).cls==='err').length;
          return (
            <div key={g.id} className="card" style={{borderLeft:`4px solid ${g.cor}`}}>
              <div style={{display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'}}>
                <div style={{width:40,height:40,borderRadius:10,background:g.cor+'22',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <Icon name="users" size={20} style={{color:g.cor}}/>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:15}}>{g.nome}</div>
                  <div style={{fontSize:12,color:'var(--ink-3)',marginTop:2}}>
                    <span><Icon name="user" size={11}/> {g.responsavel}</span>
                    <span style={{marginLeft:12}}><Icon name="mail" size={11}/> {g.email}</span>
                    <span style={{marginLeft:12}}><Icon name="clock" size={11}/> Prazo: {g.prazoMeses} mês/meses</span>
                  </div>
                </div>
                <div style={{display:'flex',gap:12,alignItems:'center'}}>
                  <div style={{textAlign:'center'}}>
                    <div style={{fontWeight:700,fontSize:18,fontFamily:"'JetBrains Mono',monospace"}}>{ga.length}</div>
                    <div style={{fontSize:10,color:'var(--ink-4)'}}>Ativos</div>
                  </div>
                  {venc > 0 && <div style={{textAlign:'center'}}>
                    <div style={{fontWeight:700,fontSize:18,color:'var(--err)',fontFamily:"'JetBrains Mono',monospace"}}>{venc}</div>
                    <div style={{fontSize:10,color:'var(--err)'}}>Vencidos</div>
                  </div>}
                  <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(g)}><Icon name="edit" size={13}/></button>
                  <button className="btn btn-danger btn-sm" onClick={()=>del(g.id)}><Icon name="trash" size={13}/></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
      {modal && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:480}}>
            <button className="close-x" onClick={()=>setModal(null)}><Icon name="x" size={16}/></button>
            <h3>{modal==='new'?'Novo Grupo de Controle':'Editar Grupo'}</h3>
            <p style={{fontSize:13,color:'var(--ink-3)',marginBottom:20}}>Defina o grupo responsável pela vistoria e o prazo de recorrência</p>
            <div className="field"><label>Nome do Grupo <span className="req">*</span></label><input value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} placeholder="Ex: Bombeiros"/></div>
            <div className="field"><label>Responsável</label><input value={form.responsavel} onChange={e=>setForm(f=>({...f,responsavel:e.target.value}))} placeholder="Nome completo"/></div>
            <div className="field"><label>Email do Responsável <span className="req">*</span></label><input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="responsavel@email.com.br"/></div>
            <div className="seg-field-row">
              <div className="field"><label>Cor de identificação</label><input type="color" value={form.cor} onChange={e=>setForm(f=>({...f,cor:e.target.value}))} style={{height:38,padding:'4px 8px',cursor:'pointer'}}/></div>
              <div className="field"><label>Prazo de Vistoria</label>
                <select value={form.prazoMeses} onChange={e=>setForm(f=>({...f,prazoMeses:parseInt(e.target.value)}))}>
                  {prazoOpts.map(p=><option key={p} value={p}>{p===1?'Mensal':p===2?'Bimestral':p===3?'Trimestral':p===6?'Semestral':p===12?'Anual':'A cada '+p+' meses'}</option>)}
                </select>
              </div>
            </div>
            {err && <p style={{color:'var(--err)',fontSize:12,marginBottom:12}}>{err}</p>}
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Salvando...':'Salvar Grupo'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Nova Vistoria Screen ──
   `codigoInicial`: vem do deep-link do QR Code (App() captura ?ativo=<codigo>
   na URL e repassa aqui — ver DOC/ADR/0012). Assim que a lista de ativos
   carrega, se o código bater com um ativo existente, ele já entra
   pré-selecionado — quem escaneou o QR não precisa procurar na lista. */
export function SegVistoriasScreen({ user, codigoInicial }) {
  const [ativos, setAtivos]       = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [ativoId, setAtivoId]     = useState('');
  const [avisoDeepLink, setAvisoDeepLink] = useState('');
  const [status, setStatus]       = useState('Aprovado');
  const [obs, setObs]             = useState('');
  const [foto, setFoto]           = useState(null); // File
  const [fotoPreview, setFotoPreview] = useState(null); // object URL local
  const [proximaData, setProxima] = useState('');
  const [busy, setBusy]           = useState(false);
  const [done, setDone]           = useState(false);
  const [err, setErr]             = useState('');
  const fileRef                   = useRef();

  const refresh = async () => {
    setCarregando(true);
    try {
      const a = await API.seg.ativos();
      setAtivos(a.ativos||[]);
      if (codigoInicial) {
        const alvo = (a.ativos||[]).find(x => x.codigo === codigoInicial);
        if (alvo) setAtivoId(alvo.id);
        else setAvisoDeepLink(`Ativo "${codigoInicial}" (do QR Code) não foi encontrado — selecione manualmente.`);
      }
    }
    catch (e) { setErr(e.message || 'Não foi possível carregar os ativos.'); }
    setCarregando(false);
  };
  useEffect(()=>{ refresh(); },[]);

  const ativo = ativos.find(a=>a.id===ativoId);

  useEffect(()=>{
    if (!ativo) return;
    /* Sugestão local — o valor definitivo é recalculado no servidor, no
       momento em que a vistoria é gravada. Prioriza a periodicidade em DIAS
       do próprio ativo (diária/semanal/quinzenal) quando existe; senão usa
       o prazo em meses do ativo como aproximação do prazo do grupo (mesma
       simplificação que a tela já fazia antes de periodicidade em dias
       existir). */
    if (ativo.periodicidadeDias) {
      setProxima(calcProxima(new Date().toISOString().split('T')[0], ativo.periodicidadeDias, 'dias'));
    } else if (ativo.grupoId) {
      setProxima(calcProxima(new Date().toISOString().split('T')[0], ativo.periodicidade || 12));
    }
  },[ativoId]);

  const handleFoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (fotoPreview) URL.revokeObjectURL(fotoPreview);
    setFoto(file);
    setFotoPreview(URL.createObjectURL(file));
  };

  const limparFoto = () => {
    if (fotoPreview) URL.revokeObjectURL(fotoPreview);
    setFoto(null); setFotoPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    if (!ativoId) { setErr('Selecione o ativo.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await API.seg.registrarVistoria({ ativoId, status, obs: obs.trim(), proximaVistoria: proximaData || undefined });
      if (foto) {
        /* A vistoria já está gravada mesmo se o envio da foto falhar aqui —
           é preferível ter o registro sem foto do que perder a vistoria
           inteira por causa de uma rede instável no upload da imagem. */
        try { await API.seg.anexarFoto(r.vistoria.id, foto); }
        catch (e) { setErr('Vistoria registrada, mas a foto não foi enviada: ' + (e.message || 'erro desconhecido') + '. Tente reabrir o ativo e anexar de novo.'); }
      }
      setDone(true);
    } catch (e) {
      setErr(e.message || 'Não foi possível registrar a vistoria.');
    }
    setBusy(false);
  };

  if (!hasPerm(user,'seguranca','create')) return <Blocked/>;

  if (done) return (
    <div className="main-pad">
      <div className="card" style={{textAlign:'center',padding:48}}>
        <Icon name="check-circle" size={48} style={{margin:'0 auto 16px',display:'block',color:'var(--ok)'}}/>
        <h3 style={{fontSize:20,marginBottom:8}}>Vistoria registrada!</h3>
        <p style={{color:'var(--ink-3)',fontSize:13,marginBottom:24}}>
          {ativo?.nome}{ativo?.grupoNome ? ` — ${ativo.grupoNome}` : ''}
        </p>
        <div style={{display:'flex',gap:12,justifyContent:'center'}}>
          <button className="btn btn-ghost" onClick={()=>{setDone(false);setAtivoId('');setObs('');limparFoto();setErr('');refresh();}}>Nova Vistoria</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>📷 Registrar Vistoria</h2><p>Documente a inspeção com foto e observações</p></div>
      </div>
      {avisoDeepLink && (
        <div className="card" style={{ padding:12,marginBottom:16,background:'var(--warn-bg)',border:'1px solid var(--warn)',fontSize:12,color:'var(--warn)' }}>
          {avisoDeepLink}
        </div>
      )}
      {carregando && <div className="card" style={{ padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16 }}>Carregando ativos…</div>}
      {!carregando && (
      <div className="seg-cols2">
        {/* Coluna esquerda */}
        <div>
          <div className="card" style={{marginBottom:16}}>
            <h3 style={{fontSize:14,fontWeight:600,marginBottom:16}}>1. Selecione o Ativo</h3>
            <div className="field">
              <label>Ativo <span className="req">*</span></label>
              <select value={ativoId} onChange={e=>setAtivoId(e.target.value)}>
                <option value="">Selecione o ativo a ser vistoriado...</option>
                {ativos.map(a=>{
                  const due = segDueStatus(a);
                  return <option key={a.id} value={a.id}>{due.cls==='err'?'🔴':due.cls==='warn'?'⚠️':'✅'} #{a.codigo} — {a.nome}{a.lojaNome?` — ${a.lojaNome}`:''} ({a.localizacao})</option>;
                })}
              </select>
            </div>
            {ativo && (
              <div style={{background:'var(--surface-2)',borderRadius:8,padding:12,fontSize:12,marginTop:8}}>
                <div style={{fontWeight:600,marginBottom:4}}>{ativo.nome}</div>
                <div style={{color:'var(--ink-3)'}}><Icon name="map-pin" size={11}/> {ativo.localizacao}</div>
                <div style={{color:'var(--ink-3)',marginTop:4}}>{ativo.lojaNome ? `🏬 ${ativo.lojaNome}` : 'Comum — Área do Shopping'}</div>
                {ativo.grupoNome && <div style={{color:ativo.grupoCor||'#2563eb',marginTop:4,fontWeight:600}}><Icon name="users" size={11}/> {ativo.grupoNome}</div>}
              </div>
            )}
          </div>

          <div className="card" style={{marginBottom:16}}>
            <h3 style={{fontSize:14,fontWeight:600,marginBottom:16}}>2. Resultado da Vistoria</h3>
            <div className="field">
              <label>Status</label>
              <div style={{display:'flex',gap:8}}>
                {['Aprovado','Reprovado','Pendente'].map(s=>(
                  <button key={s} onClick={()=>setStatus(s)} style={{flex:1,height:38,border:'1.5px solid',borderRadius:8,cursor:'pointer',fontFamily:'inherit',fontSize:12,fontWeight:600,
                    borderColor:status===s?(s==='Aprovado'?'var(--ok)':s==='Reprovado'?'var(--err)':'var(--warn)'):'var(--line)',
                    background:status===s?(s==='Aprovado'?'var(--ok-bg)':s==='Reprovado'?'var(--err-bg)':'var(--warn-bg)'):'var(--surface)',
                    color:status===s?(s==='Aprovado'?'var(--ok)':s==='Reprovado'?'var(--err)':'var(--warn)'):'var(--ink-3)'
                  }}>{s}</button>
                ))}
              </div>
            </div>
            <div className="field">
              <label>Observações / Notas</label>
              <textarea rows={4} value={obs} onChange={e=>setObs(e.target.value)} placeholder="Descreva o estado do ativo, irregularidades, ações necessárias..."/>
            </div>
            <div className="field">
              <label>Próxima Vistoria</label>
              <input type="date" value={proximaData} onChange={e=>setProxima(e.target.value)}/>
              <div className="hint">Sugestão calculada pelo prazo do grupo — ajuste se necessário.</div>
            </div>
          </div>
        </div>

        {/* Coluna direita — foto */}
        <div>
          <div className="card">
            <h3 style={{fontSize:14,fontWeight:600,marginBottom:8}}>3. Foto da Vistoria</h3>
            <p style={{fontSize:12,color:'var(--ink-3)',marginBottom:16}}>Use a câmera do dispositivo para registrar o estado atual do ativo. A foto usa obrigatoriamente a câmera traseira.</p>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" capture="environment" style={{display:'none'}} onChange={handleFoto}/>
            {fotoPreview ? (
              <div>
                <img src={fotoPreview} alt="Preview" className="seg-photo-preview"/>
                <button className="btn btn-ghost btn-sm" style={{marginTop:10,width:'100%'}} onClick={limparFoto}><Icon name="x" size={13}/> Remover foto</button>
              </div>
            ) : (
              <div className="seg-photo-placeholder" onClick={()=>fileRef.current.click()}>
                <Icon name="camera" size={28}/>
                <span>Toque para abrir a câmera</span>
                <span style={{fontSize:11}}>Câmera traseira automática</span>
              </div>
            )}
            <button className="btn btn-gold" style={{width:'100%',marginTop:16,height:42}} onClick={()=>fileRef.current.click()}>
              <Icon name="camera" size={16}/> {fotoPreview?'Refazer Foto':'Tirar Foto'}
            </button>
          </div>

          {/* Envio */}
          <div className="card" style={{marginTop:16}}>
            <h3 style={{fontSize:14,fontWeight:600,marginBottom:8}}>4. Finalizar</h3>
            {err && <p style={{color:'var(--err)',fontSize:12,marginBottom:12}}>{err}</p>}
            <button className="btn btn-primary btn-lg" style={{width:'100%'}} onClick={submit} disabled={busy}>
              {busy?'Registrando...':'✓ Confirmar Vistoria'}
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}


/* ── Tipos de Ativo Screen ── */
export function SegTiposAtivoScreen({ user }) {
  const [tipos, setTipos]   = useState([]);
  const [ativos, setAtivos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga]   = useState('');
  const [modal, setModal]   = useState(null); // null | 'new' | tipo
  const [form, setForm]     = useState({ nome:'', icone:'🔴', descricao:'' });
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try {
      const [t, a] = await Promise.all([API.seg.tipos(), API.seg.ativos()]);
      setTipos(t.tipos||[]); setAtivos(a.ativos||[]);
    } catch (e) {
      setErroCarga(e.message || 'Não foi possível carregar os tipos.');
    }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, []);

  if (!hasPerm(user,'seguranca','edit')) return <Blocked/>;

  const openNew = () => { setForm({ nome:'', icone:'🔴', descricao:'' }); setErr(''); setModal('new'); };
  const openEdit = (t) => { setForm({ nome:t.nome, icone:t.icone||'🔴', descricao:t.descricao||'' }); setErr(''); setModal(t); };

  const save = async () => {
    if (!form.nome.trim()) { setErr('Informe o nome do tipo.'); return; }
    setBusy(true); setErr('');
    try {
      if (modal === 'new') await API.seg.criarTipo(form);
      else                 await API.seg.editarTipo(modal.id, form);
      await refresh(); setModal(null);
    } catch (e) {
      setErr(e.message || 'Não foi possível salvar o tipo.');
    }
    setBusy(false);
  };

  const del = async (id) => {
    const tipo = tipos.find(t => t.id === id);
    if (!confirm('Remover tipo "' + (tipo?.nome||'') + '"?')) return;
    try { await API.seg.removerTipo(id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível remover o tipo.'); }
  };

  const iconeOpts = ['🔴','🟠','🟡','🟢','🔵','🟣','⚫','🔥','💧','🌫️','🚪','💡','📷','⚡','🔧','🛡️','⚠️','🔒','🚨','📡'];

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title">
          <h2>🗂️ Tipos de Ativo</h2>
          <p>Gerencie os tipos de ativos disponíveis para cadastro</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>
          <Icon name="plus" size={14}/> Novo Tipo
        </button>
      </div>

      {carregando && <div className="card" style={{ padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16 }}>Carregando tipos…</div>}
      {erroCarga && (
        <div className="card" style={{ padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2' }}>
          <div style={{ color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6 }}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && (tipos.length === 0 ? (
        <div className="card" style={{textAlign:'center', padding:40, color:'var(--ink-3)'}}>
          <Icon name="list" size={32} style={{margin:'0 auto 12px', display:'block'}}/>
          <p>Nenhum tipo cadastrado.</p>
          <button className="btn btn-primary" style={{marginTop:16}} onClick={openNew}>
            <Icon name="plus" size={14}/> Cadastrar primeiro tipo
          </button>
        </div>
      ) : (
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14}}>
          {tipos.map(t => {
            const qtd = ativos.filter(a => a.tipoId === t.id).length;
            return (
              <div key={t.id} className="card" style={{display:'flex', flexDirection:'column', gap:12}}>
                <div style={{display:'flex', alignItems:'center', gap:14}}>
                  <div style={{
                    width:48, height:48, borderRadius:12,
                    background:'var(--surface-2)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:24, flexShrink:0,
                    border:'1px solid var(--line)'
                  }}>
                    {t.icone||'🔴'}
                  </div>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontWeight:600, fontSize:15, color:'var(--ink)'}}>{t.nome}</div>
                    {t.descricao && <div style={{fontSize:12, color:'var(--ink-3)', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{t.descricao}</div>}
                  </div>
                </div>
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:10, borderTop:'1px solid var(--line)'}}>
                  <span style={{
                    display:'inline-flex', alignItems:'center', gap:5,
                    fontSize:12, fontWeight:600,
                    color: qtd > 0 ? 'var(--navy)' : 'var(--ink-4)',
                    background: qtd > 0 ? 'var(--info-bg)' : 'var(--surface-2)',
                    padding:'3px 10px', borderRadius:999
                  }}>
                    <Icon name="shield" size={11}/> {qtd} ativo{qtd !== 1 ? 's' : ''}
                  </span>
                  <div style={{display:'flex', gap:6}}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}>
                      <Icon name="edit" size={13}/>
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => del(t.id)}>
                      <Icon name="trash" size={13}/>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Modal novo/editar tipo */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{maxWidth:440}}>
            <button className="close-x" onClick={() => setModal(null)}><Icon name="x" size={16}/></button>
            <h3>{modal === 'new' ? 'Novo Tipo de Ativo' : 'Editar Tipo de Ativo'}</h3>
            <p style={{fontSize:13, color:'var(--ink-3)', marginBottom:20}}>
              Defina o nome, ícone e descrição do tipo de ativo de segurança
            </p>

            <div className="field">
              <label>Ícone de Identificação</label>
              <div style={{display:'flex', flexWrap:'wrap', gap:6, marginTop:4}}>
                {iconeOpts.map(ico => (
                  <button key={ico} onClick={() => setForm(f => ({...f, icone:ico}))} style={{
                    width:38, height:38, fontSize:20, cursor:'pointer',
                    border: form.icone === ico ? '2px solid var(--navy)' : '1.5px solid var(--line)',
                    borderRadius:8, background: form.icone === ico ? 'var(--info-bg)' : 'var(--surface-2)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    transition:'all 0.15s',
                  }}>{ico}</button>
                ))}
              </div>
            </div>

            <div className="field">
              <label>Nome do Tipo <span className="req">*</span></label>
              <input
                value={form.nome}
                onChange={e => setForm(f => ({...f, nome:e.target.value}))}
                placeholder="Ex: Extintor, Hidrante, Sensor de Fumaça..."
                autoFocus
              />
            </div>

            <div className="field">
              <label>Descrição (opcional)</label>
              <textarea
                rows={3}
                value={form.descricao}
                onChange={e => setForm(f => ({...f, descricao:e.target.value}))}
                placeholder="Descreva o tipo de ativo e sua finalidade..."
              />
            </div>

            {form.nome && (
              <div style={{
                background:'var(--surface-2)', borderRadius:10, padding:'12px 16px',
                display:'flex', alignItems:'center', gap:12, marginBottom:16,
                border:'1px solid var(--line)'
              }}>
                <div style={{fontSize:24}}>{form.icone}</div>
                <div>
                  <div style={{fontWeight:600, fontSize:14}}>{form.nome}</div>
                  {form.descricao && <div style={{fontSize:12, color:'var(--ink-3)', marginTop:2}}>{form.descricao}</div>}
                </div>
              </div>
            )}

            {err && <p style={{color:'var(--err)', fontSize:12, marginBottom:12}}>{err}</p>}

            <div style={{display:'flex', gap:10, justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>
                {busy ? 'Salvando...' : 'Salvar Tipo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Dashboard de Segurança ── */
export function SegDashboardScreen({ user }) {
  const [vistorias, setVistorias] = useState([]);
  const [ativos, setAtivos]       = useState([]);
  const [grupos, setGrupos]       = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga]   = useState('');
  const [filtroGrp, setFiltroGrp] = useState('todos');
  const [filtroSts, setFiltroSts] = useState('todos');

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try {
      const [v, a, g] = await Promise.all([
        API.seg.vistorias({
          grupo_id: filtroGrp !== 'todos' ? filtroGrp : undefined,
          status:   filtroSts !== 'todos' ? filtroSts : undefined,
        }),
        API.seg.ativos(),
        API.seg.grupos(),
      ]);
      setVistorias(v.vistorias||[]); setAtivos(a.ativos||[]); setGrupos(g.grupos||[]);
    } catch (e) {
      setErroCarga(e.message || 'Não foi possível carregar o painel.');
    }
    setCarregando(false);
  };
  useEffect(()=>{ refresh(); },[filtroGrp,filtroSts]);

  if (!hasPerm(user,'seguranca','view')) return <Blocked/>;

  const vencidos = ativos.filter(a=>segDueStatus(a).cls==='err').length;
  const proximos = ativos.filter(a=>segDueStatus(a).cls==='warn').length;
  const okCount  = ativos.filter(a=>segDueStatus(a).cls==='ok').length;

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>📊 Dashboard — Segurança</h2><p>Visão geral de ativos e histórico de vistorias</p></div>
      </div>

      {erroCarga && (
        <div className="card" style={{ padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2' }}>
          <div style={{ color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6 }}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      <div className="seg-stat-row">
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--ink)'}}>{ativos.length}</div><div className="sl">Ativos cadastrados</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--ok)'}}>{okCount}</div><div className="sl">Em dia</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--warn)'}}>{proximos}</div><div className="sl">Próximos do prazo</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--err)'}}>{vencidos}</div><div className="sl">Vencidos</div></div>
      </div>

      {/* Por grupo */}
      {grupos.length > 0 && (
        <div className="card" style={{marginBottom:20}}>
          <div className="card-head"><h3>Situação por Grupo</h3></div>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {grupos.map(g=>{
              const ga   = ativos.filter(a=>a.grupoId===g.id);
              const gv   = vistorias.filter(v=>v.grupoId===g.id);
              const gErr = ga.filter(a=>segDueStatus(a).cls==='err').length;
              const gWrn = ga.filter(a=>segDueStatus(a).cls==='warn').length;
              return (
                <div key={g.id} style={{display:'flex',alignItems:'center',gap:16,padding:'12px 0',borderBottom:'1px solid var(--line)'}}>
                  <div style={{width:12,height:12,borderRadius:'50%',background:g.cor,flexShrink:0}}/>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:13}}>{g.nome}</div>
                    <div style={{fontSize:11,color:'var(--ink-3)'}}>{g.responsavel} · {ga.length} ativos · {gv.length} vistorias</div>
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    {gErr>0 && <span className="status-pill err"><span className="ind"/>{gErr} vencido{gErr>1?'s':''}</span>}
                    {gWrn>0 && <span className="status-pill warn"><span className="ind"/>{gWrn} prox.</span>}
                    {gErr===0&&gWrn===0 && <span className="status-pill ok"><span className="ind"/>Em dia</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Histórico de vistorias */}
      <div className="card">
        <div className="card-head">
          <h3>Histórico de Vistorias</h3>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <select style={{height:30,fontSize:12,borderRadius:6,border:'1px solid var(--line)',padding:'0 8px',background:'var(--surface-2)'}} value={filtroGrp} onChange={e=>setFiltroGrp(e.target.value)}>
              <option value="todos">Todos os grupos</option>
              {grupos.map(g=><option key={g.id} value={g.id}>{g.nome}</option>)}
            </select>
            <select style={{height:30,fontSize:12,borderRadius:6,border:'1px solid var(--line)',padding:'0 8px',background:'var(--surface-2)'}} value={filtroSts} onChange={e=>setFiltroSts(e.target.value)}>
              <option value="todos">Todos os status</option>
              {['Aprovado','Reprovado','Pendente'].map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        {carregando ? (
          <div style={{textAlign:'center',padding:32,color:'var(--ink-3)'}}>Carregando…</div>
        ) : vistorias.length===0 ? (
          <div style={{textAlign:'center',padding:32,color:'var(--ink-3)'}}>
            <Icon name="file-text" size={28} style={{margin:'0 auto 8px',display:'block'}}/>
            <p>Nenhuma vistoria encontrada.</p>
          </div>
        ) : vistorias.map(v=>(
          <div key={v.id} className="seg-insp-row">
            <div className="seg-insp-header">
              <div>
                <div className="seg-insp-who">{v.ativoNome} <span style={{fontSize:11,color:'var(--ink-3)',fontWeight:400}}>— {v.ativoLocalizacao}{v.lojaNome?` — 🏬 ${v.lojaNome}`:''}</span></div>
                <div className="seg-insp-when">{new Date(v.criadoEm).toLocaleString('pt-BR')} · {v.vistoriador} · {v.grupoNome}</div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                {v.temFoto && <SegFoto vistoriaId={v.id} className="seg-insp-thumb"/>}
                <span className={`status-pill ${v.status==='Aprovado'?'ok':v.status==='Reprovado'?'err':'warn'}`}><span className="ind"/>{v.status}</span>
              </div>
            </div>
            {v.obs && <div className="seg-insp-obs">💬 {v.obs}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
